import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Plus, Search, Upload, X, Tag, UserMinus, AlertTriangle } from 'lucide-react';
import { Panel, Section, Button, Badge, LoadingSpinner } from './components';
import apiService from './services/api';

function AddSubscriberModal({ tags, onClose, onSave }) {
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', tag_ids: [] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const toggleTag = (tagId) => {
    setForm(prev => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter(id => id !== tagId)
        : [...prev.tag_ids, tagId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) { setError('Email is required'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await apiService.createSubscriber(form);
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to create subscriber');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-sm font-semibold">Add Subscriber</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        {error && (
          <div className="mx-4 mt-3 p-2 bg-red-50 border border-red-200 rounded">
            <p className="text-xs text-red-800">{error}</p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none"
              placeholder="name@example.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
              <input type="text" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})}
                className="w-full px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
              <input type="text" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})}
                className="w-full px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none" />
            </div>
          </div>
          {tags.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tags</label>
              <div className="flex flex-wrap gap-1">
                {tags.map(tag => (
                  <button key={tag.id} type="button" onClick={() => toggleTag(tag.id)}
                    className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                      form.tag_ids.includes(tag.id)
                        ? 'bg-red-800 text-white border-red-800'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-red-800'
                    }`}>{tag.name}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t">
            <Button type="submit" variant="primary" size="sm" disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Subscriber'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportCsvModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await apiService.importSubscribersCsv(formData);
      setResult(data);
    } catch (err) {
      setError(err.message || 'Import failed');
    }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-sm font-semibold">Import Subscribers from CSV</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-4 space-y-3">
          {!result ? (
            <>
              <p className="text-xs text-gray-500">
                Upload a Mailchimp CSV export. Expected columns: Email Address, First Name, Last Name, TAGS.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <input ref={fileRef} type="file" accept=".csv" onChange={e => setFile(e.target.files[0])}
                  className="hidden" />
                {file ? (
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    <button onClick={() => setFile(null)} className="text-xs text-red-600 mt-1">Remove</button>
                  </div>
                ) : (
                  <button onClick={() => fileRef.current?.click()} className="text-sm text-gray-500 hover:text-red-800">
                    <Upload className="w-5 h-5 mx-auto mb-1" />
                    Click to select CSV file
                  </button>
                )}
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
              <div className="flex gap-2">
                <Button variant="primary" size="sm" onClick={handleUpload} disabled={!file || uploading}>
                  {uploading ? 'Importing...' : 'Import'}
                </Button>
                <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-sm font-medium text-green-800 mb-2">Import Complete</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-gray-500">Created:</span> <span className="font-semibold">{result.created}</span></div>
                  <div><span className="text-gray-500">Updated:</span> <span className="font-semibold">{result.updated}</span></div>
                  <div><span className="text-gray-500">Skipped:</span> <span className="font-semibold">{result.skipped}</span></div>
                  <div><span className="text-gray-500">Errors:</span> <span className="font-semibold">{result.errors?.length || 0}</span></div>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="text-xs text-red-600">
                  {result.errors.slice(0, 5).map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
              <Button variant="primary" size="sm" onClick={() => { onDone(); onClose(); }}>Done</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SubscribersPage() {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState(null);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscribedFilter, setSubscribedFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (subscribedFilter) params.subscribed = subscribedFilter;
      if (sourceFilter) params.source = sourceFilter;

      const [subData, statsData, tagData] = await Promise.all([
        apiService.getSubscribers(params),
        apiService.getSubscriberStats(),
        apiService.getTags(),
      ]);

      let subs = Array.isArray(subData) ? subData : subData.results || [];

      // Client-side tag filter (DRF filterset doesn't support M2M easily)
      if (tagFilter) {
        subs = subs.filter(s => s.tag_names && s.tag_names.includes(tagFilter));
      }

      setSubscribers(subs);
      setStats(statsData);
      setTags(Array.isArray(tagData) ? tagData : tagData.results || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [searchTerm, subscribedFilter, sourceFilter, tagFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (sub) => {
    if (!window.confirm(`Remove subscriber ${sub.email}?`)) return;
    try {
      await apiService.deleteSubscriber(sub.id);
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggleSubscribed = async (sub) => {
    try {
      await apiService.updateSubscriber(sub.id, { subscribed: !sub.subscribed });
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 leading-none">Subscribers</h1>
            <p className="text-xs text-gray-500 mt-0.5">Manage your email subscriber list</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowImportModal(true)}>
              <Upload className="w-3.5 h-3.5 mr-1" />Import CSV
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" />Add Subscriber
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <Panel>
            <div className="flex items-center gap-2 p-1">
              <div className="p-1.5 bg-blue-50 rounded"><Users className="w-4 h-4 text-blue-600" /></div>
              <div><p className="text-xs text-gray-500">Total</p><p className="text-sm font-semibold">{stats?.total || 0}</p></div>
            </div>
          </Panel>
          <Panel>
            <div className="flex items-center gap-2 p-1">
              <div className="p-1.5 bg-green-50 rounded"><Users className="w-4 h-4 text-green-600" /></div>
              <div><p className="text-xs text-gray-500">Active</p><p className="text-sm font-semibold text-green-700">{stats?.active || 0}</p></div>
            </div>
          </Panel>
          <Panel>
            <div className="flex items-center gap-2 p-1">
              <div className="p-1.5 bg-red-50 rounded"><UserMinus className="w-4 h-4 text-red-600" /></div>
              <div><p className="text-xs text-gray-500">Unsubscribed</p><p className="text-sm font-semibold text-red-600">{stats?.unsubscribed || 0}</p></div>
            </div>
          </Panel>
          <Panel>
            <div className="flex items-center gap-2 p-1">
              <div className="p-1.5 bg-purple-50 rounded"><Tag className="w-4 h-4 text-purple-600" /></div>
              <div><p className="text-xs text-gray-500">Tags</p><p className="text-sm font-semibold">{tags.length}</p></div>
            </div>
          </Panel>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by email or name..." className="w-full pl-8 pr-3 py-1.5 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
          </div>
          <select value={subscribedFilter} onChange={e => setSubscribedFilter(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded outline-none">
            <option value="">All Status</option>
            <option value="true">Subscribed</option>
            <option value="false">Unsubscribed</option>
          </select>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded outline-none">
            <option value="">All Sources</option>
            <option value="mailchimp_import">Mailchimp Import</option>
            <option value="website_signup">Website Signup</option>
            <option value="manual">Manual</option>
            <option value="client_sync">Client Sync</option>
          </select>
          {tags.length > 0 && (
            <select value={tagFilter} onChange={e => setTagFilter(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded outline-none">
              <option value="">All Tags</option>
              {tags.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          )}
        </div>

        {/* Subscriber Table */}
        <Panel>
          <Section title={`All Subscribers (${subscribers.length})`}>
            {loading ? <LoadingSpinner size="sm" message="Loading subscribers..." /> : error ? (
              <div className="text-center py-8">
                <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-xs text-red-600">{error}</p>
                <Button variant="secondary" size="sm" onClick={loadData} className="mt-2">Retry</Button>
              </div>
            ) : subscribers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-1">No subscribers yet</p>
                <p className="text-xs text-gray-400 mb-3">Import from Mailchimp or add subscribers manually</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="secondary" size="sm" onClick={() => setShowImportModal(true)}>
                    <Upload className="w-3.5 h-3.5 mr-1" />Import CSV
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                    <Plus className="w-3.5 h-3.5 mr-1" />Add Subscriber
                  </Button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Tags</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Source</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Added</th>
                      <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscribers.map(sub => (
                      <tr key={sub.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 text-xs text-gray-900">{sub.email}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-700">{sub.full_name || '--'}</td>
                        <td className="px-2 py-1.5">
                          <div className="flex flex-wrap gap-0.5">
                            {sub.tag_names && sub.tag_names.map(t => (
                              <span key={t} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">{t}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge small variant={sub.subscribed ? 'success' : 'error'}>
                            {sub.subscribed ? 'Active' : 'Unsubscribed'}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-500">
                          {sub.source === 'mailchimp_import' ? 'Mailchimp' :
                           sub.source === 'website_signup' ? 'Website' :
                           sub.source === 'client_sync' ? 'Client' : 'Manual'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-500">
                          {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '--'}
                        </td>
                        <td className="px-2 py-1.5 text-right space-x-1">
                          <button onClick={() => handleToggleSubscribed(sub)}
                            className={`text-xs ${sub.subscribed ? 'text-amber-600' : 'text-green-600'} hover:underline`}>
                            {sub.subscribed ? 'Unsub' : 'Resub'}
                          </button>
                          <button onClick={() => handleDelete(sub)} className="text-xs text-red-600 hover:underline">Del</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </Panel>
      </div>

      {showAddModal && (
        <AddSubscriberModal
          tags={tags}
          onClose={() => setShowAddModal(false)}
          onSave={() => { setShowAddModal(false); loadData(); }}
        />
      )}

      {showImportModal && (
        <ImportCsvModal
          onClose={() => setShowImportModal(false)}
          onDone={loadData}
        />
      )}
    </div>
  );
}

export default SubscribersPage;
